import type { QueryClient } from "@tanstack/react-query";
import type { WorkoutSessionState } from "@/hooks/use-workout-session";
import { wtKeys } from "@/lib/weight-training-keys";
import { workoutSessionStateSchema } from "./session-state";

type Storage = Pick<globalThis.Storage, "getItem" | "setItem" | "removeItem">;
const receiptKey = (accountId: string) =>
  `hiive:strength:acknowledged:v1:${accountId}`;
/**
 * Exported so cross-tab listeners match the key this module actually writes.
 * A hand-copied literal elsewhere goes silently deaf the moment this version
 * changes.
 */
export const workoutJournalStorageKey = (accountId: string) =>
  `hiive:strength:session:v1:${accountId}`;
const key = workoutJournalStorageKey;

/** Stop the clock without losing sets; only the owner can synchronize this result. */
export function freezeWorkoutSession(
  session: WorkoutSessionState,
  now: number,
  disposition: "completed" | "discarded" = "completed",
) {
  workoutSessionStateSchema.parse(session);
  if (!session.workoutId || !session.startedAt)
    throw new Error("There is no workout to finish");
  if (session.endedAt)
    return {
      ...session,
      workoutId: session.workoutId,
      startedAt: session.startedAt,
      endedAt: session.endedAt,
    };
  if (!Number.isFinite(now)) throw new Error("Invalid workout stop time");
  // A delayed control must not put sets already logged outside the workout.
  const lastSetAt = session.exercises.reduce(
    (latest, exercise) =>
      exercise.sets.reduce(
        (at, set) =>
          Math.max(at, set.completedAt ? Date.parse(set.completedAt) : 0),
        latest,
      ),
    Date.parse(session.startedAt),
  );
  return {
    ...session,
    workoutId: session.workoutId,
    startedAt: session.startedAt,
    endedAt: new Date(Math.max(lastSetAt, now)).toISOString(),
    disposition,
    restTimer: { active: false, restEndTime: null, totalSeconds: 0 },
  };
}

export function readWorkoutJournal(
  storage: Storage,
  accountId: string,
): WorkoutSessionState | null {
  const raw = storage.getItem(key(accountId));
  if (!raw) return null;
  try {
    const entry = JSON.parse(raw);
    if (entry.schemaVersion !== 1 || entry.accountId !== accountId)
      throw new Error();
    const session = workoutSessionStateSchema.parse(entry.session);
    if (session.accountId && session.accountId !== accountId) throw new Error();
    return session;
  } catch {
    throw new Error(
      "Saved workout needs recovery. Its data has been retained.",
    );
  }
}
export function writeWorkoutJournal(
  storage: Storage,
  accountId: string,
  session: WorkoutSessionState | null,
) {
  if (!accountId) throw new Error("Sign in before recording a workout");
  if (session === null) {
    storage.removeItem(key(accountId));
    return;
  }
  workoutSessionStateSchema.parse(session);
  const receipts: unknown = JSON.parse(
    storage.getItem(receiptKey(accountId)) ?? "[]",
  );
  if (!Array.isArray(receipts) || receipts.some((id) => typeof id !== "string"))
    throw new Error(
      "Workout recovery metadata is invalid. Its data has been retained.",
    );
  if (session.workoutId && receipts.includes(session.workoutId))
    throw new Error("This workout has already synchronized");
  if (session.accountId && session.accountId !== accountId)
    throw new Error("Workout account changed");
  const existing = readWorkoutJournal(storage, accountId);
  if (
    existing?.workoutId &&
    (existing.workoutId !== session.workoutId ||
      existing.startedAt !== session.startedAt)
  )
    throw new Error(
      "Another workout is saved on this device. Its data has been retained.",
    );
  if (
    existing?.endedAt &&
    JSON.stringify(existing) !==
      JSON.stringify(workoutSessionStateSchema.parse(session))
  )
    throw new Error(
      "This workout is awaiting synchronization. Its final result cannot change.",
    );
  storage.setItem(
    key(accountId),
    JSON.stringify({ schemaVersion: 1, accountId, session }),
  );
}

export function getCurrentWorkoutJournal(
  queryClient: QueryClient,
  accountId?: string,
) {
  if (!accountId) return null;
  return (
    readWorkoutJournal(window.localStorage, accountId) ??
    queryClient.getQueryData<WorkoutSessionState>(
      wtKeys.activeWorkout(accountId),
    )
  );
}
export function seedWorkoutJournal(
  queryClient: QueryClient,
  session: WorkoutSessionState & { accountId: string },
) {
  const existing = getCurrentWorkoutJournal(queryClient, session.accountId);
  if (existing?.workoutId && existing.workoutId !== session.workoutId)
    throw new Error("Finish your current workout first");
  writeWorkoutJournal(window.localStorage, session.accountId, session);
  queryClient.setQueryData(wtKeys.activeWorkout(session.accountId), session);
}

export function acknowledgeWorkoutJournal(
  storage: Storage,
  accountId: string,
  workoutId: string,
  endedAt: string,
): boolean {
  const current = readWorkoutJournal(storage, accountId);
  if (
    !current ||
    current.workoutId !== workoutId ||
    current.endedAt !== endedAt
  )
    return false;
  const receipts: unknown = JSON.parse(
    storage.getItem(receiptKey(accountId)) ?? "[]",
  );
  if (!Array.isArray(receipts) || receipts.some((id) => typeof id !== "string"))
    throw new Error(
      "Workout recovery metadata is invalid. Its data has been retained.",
    );
  storage.setItem(
    receiptKey(accountId),
    JSON.stringify([...new Set([...receipts, workoutId])]),
  );
  storage.removeItem(key(accountId));
  return true;
}
